import type { Audience } from "../../../shared/goals/audience";
import { STANDARD_EVENTS } from "../../../shared/goals/events";
import { Icon } from "../../components/ui/Icon";
import { found } from "../../components/lists/ProductLists";
import { listFor, type ListView, type ListsView } from "../../lib/listsApi";

/** One event the pixel sent, by Meta's name, and how many times in the last 180 days. */
export interface PixelEvent {
  name: string;
  count: number;
}

/** An audience the owner made in their Meta ad account: Meta's id, its name and Meta's size. */
export interface CustomAudience {
  id: string;
  name: string;
  size: number | null;
}

/**
 * The goal's client lists on the Audience step (notes/AUDIENCE.md 5.5): the product's lists this goal ticks,
 * each to start with, find people like them or leave out, the product's other lists to add, a new list (saved
 * to the product), and, to start with or leave out, the people who sent one of the pixel's events and the
 * audiences the owner made in their Meta ad account.
 */

const n = (x: number) => x.toLocaleString("en-US");

/** Whether Meta can find people like them: made, refused in Meta's words, or by the count; nothing under the floor until Meta makes them. */
export function likeLine(l: ListView): string | null {
  if (l.like?.state === "ready") return "enough to find people like them";
  if (l.like?.state === "failed") return l.like.note;
  if (l.belowFloor) return null;
  return l.likeReady ? "enough to find people like them" : "Meta needs 100 to find people like them";
}

export function listsSummary(a: Audience, view: ListsView | null): string {
  const name = (id: string) => (view && listFor(view.lists, id)?.name.toLowerCase()) ?? "past clients";
  const bits = [
    ...a.lists.filter((l) => l.start).map((l) => `your ${name(l.id)}`),
    ...a.lists.filter((l) => l.like).map((l) => `people like your ${name(l.id)}`),
    ...a.lists.filter((l) => l.out).map((l) => `not your ${name(l.id)}`),
    ...a.custom.start.map((c) => c.name),
    ...a.custom.out.map((c) => `not ${c.name}`),
  ];
  if (a.events.start.length) bits.push(`people your pixel saw with ${a.events.start.join(" or ")}`);
  if (a.events.out.length) bits.push(`not people your pixel saw with ${a.events.out.join(" or ")}`);
  return bits.join(", ") || "None chosen for this goal";
}

export type Side = "start" | "out";
const SIDES: [Side, string][] = [
  ["start", "Start with"],
  ["out", "Leave out"],
];

/** One tag: its key, its words, its count when known, and a line under the rows when it has a problem. */
export interface Tag {
  key: string;
  label: string;
  count?: number | null;
  problem?: string;
}

/** Tags in two rows, to start with or to leave out; a tag sits in one row at a time. */
function TagRows({
  title,
  note,
  empty,
  unread,
  tags,
  chosen,
  editable,
  onToggle,
}: {
  title: string;
  note?: string;
  empty: string | null;
  unread: boolean;
  tags: Tag[];
  chosen: Record<Side, string[]>;
  editable: boolean;
  onToggle: (side: Side, key: string, on: boolean) => void;
}) {
  const picked = [...chosen.start, ...chosen.out];
  const problems = tags.filter((t) => t.problem && picked.includes(t.key));
  return (
    <div className="pxe">
      <div className="pxe-h">
        <b>{title}</b>
        {note && <span>{note}</span>}
      </div>
      {unread && <p className="hint">Meta did not answer just now. Reload the page to try again.</p>}
      {!tags.length && !unread && empty && <p className="hint">{empty}</p>}
      {tags.length > 0 &&
        SIDES.map(([side, label]) => (
          <div className="ev" key={side}>
            <span className="evl">{label}</span>
            <div className="tags">
              {tags.map((t) => {
                const on = chosen[side].includes(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    className={`t${on ? ` on ${side}` : ""}${on && t.problem ? " bad" : ""}`}
                    aria-pressed={on}
                    disabled={!editable || (!on && picked.includes(t.key))}
                    onClick={() => onToggle(side, t.key, on)}
                  >
                    {t.label}
                    {t.count !== undefined && t.count !== null && <small>{n(t.count)}</small>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      {problems.map((t) => (
        <p className="refused" key={t.key}>
          {t.problem}
        </p>
      ))}
    </div>
  );
}

/** The pixel's events as tags, by what people did, with their counts and any refusal from Meta. */
export function pixelTags(aud: Audience, view: ListsView, events: PixelEvent[] | null): Tag[] {
  const chosen = [...aud.events.start, ...aud.events.out];
  const names = [...(events ?? []).map((e) => e.name), ...chosen.filter((c) => !events?.some((e) => e.name === c))];
  return names.map((name): Tag => {
    const refused = view.events.find((e) => e.event === name && e.state === "failed");
    // Why: the owner reads what people did; a site's own event keeps the name the site gave it.
    const label = name === "PageView" ? "Visits to your page" : (STANDARD_EVENTS[name]?.plural ?? name);
    return { key: name, label, count: events?.find((e) => e.name === name)?.count, problem: refused ? `Meta refused ${label.toLowerCase()}: ${refused.note ?? "no reason given"}` : undefined };
  });
}

/** Start with, or leave out, the people who sent one of the pixel's events: the next audience and its summary. */
export const togglePixel = (aud: Audience, side: Side, name: string, on: boolean): [Audience, string] => [
  { ...aud, events: { ...aud.events, [side]: on ? aud.events[side].filter((x) => x !== name) : [...aud.events[side], name] } },
  `Client lists: ${on ? "not " : ""}${side === "start" ? "starting with" : "leaving out"} ${name} from the pixel`,
];

/** The audiences in the owner's Meta ad account as tags; a chosen one Meta no longer has says so. */
export function customTags(aud: Audience, audiences: CustomAudience[] | null): Tag[] {
  const chosen = [...aud.custom.start, ...aud.custom.out];
  // Why: until Meta answers, the chosen ones still show (by the name kept with them), so they can be taken off.
  return audiences === null
    ? chosen.map((c) => ({ key: c.id, label: c.name }))
    : [
        ...audiences.map((a) => ({ key: a.id, label: a.name, count: a.size })),
        ...chosen.filter((c) => !audiences.some((a) => a.id === c.id)).map((c) => ({ key: c.id, label: c.name, problem: `${c.name} is no longer in your Meta ad account.` })),
      ];
}

/** Start with, or leave out, the people in one of the owner's Meta audiences: the next audience and its summary. */
export function toggleCustom(aud: Audience, tags: Tag[], side: Side, id: string, on: boolean): [Audience, string] {
  const name = tags.find((t) => t.key === id)?.label ?? id;
  const list = on ? aud.custom[side].filter((c) => c.id !== id) : [...aud.custom[side], { id, name }];
  return [{ ...aud, custom: { ...aud.custom, [side]: list } }, `Client lists: ${on ? "not " : ""}${side === "start" ? "starting with" : "leaving out"} ${name}`];
}

/** The pixel's events, by Meta's name with their 180-day counts: the people who sent one. */
function PixelEvents({
  aud,
  view,
  events,
  unread,
  editable,
  save,
}: {
  aud: Audience;
  view: ListsView;
  events: PixelEvent[] | null;
  unread: boolean;
  editable: boolean;
  save: (next: Audience, summary: string) => void;
}) {
  const tags = pixelTags(aud, view, events);
  return (
    <TagRows
      title="From your pixel"
      note="last 180 days"
      empty={events !== null ? "Your pixel has sent no events yet." : null}
      unread={unread}
      tags={tags}
      chosen={aud.events}
      editable={editable}
      onToggle={(side, name, on) => save(...togglePixel(aud, side, name, on))}
    />
  );
}

/** The audiences the owner made in their Meta ad account, with Meta's size: the people in one. */
function MetaAudiences({
  aud,
  audiences,
  unread,
  editable,
  save,
}: {
  aud: Audience;
  audiences: CustomAudience[] | null;
  unread: boolean;
  editable: boolean;
  save: (next: Audience, summary: string) => void;
}) {
  if (audiences === null && !aud.custom.start.length && !aud.custom.out.length && !unread) return null;
  const tags = customTags(aud, audiences);
  return (
    <TagRows
      title="Audiences already in your Meta account"
      empty={audiences !== null ? "No audiences in your Meta ad account yet." : null}
      unread={unread}
      tags={tags}
      chosen={{ start: aud.custom.start.map((c) => c.id), out: aud.custom.out.map((c) => c.id) }}
      editable={editable}
      onToggle={(side, id, on) => save(...toggleCustom(aud, tags, side, id, on))}
    />
  );
}

export function ListsBlock({
  aud,
  view,
  events,
  custom,
  unread,
  editable,
  save,
  onAdd,
}: {
  aud: Audience;
  view: ListsView | null;
  /** The events the pixel sent in the last 180 days; null while Meta is asked or when it did not answer. */
  events: PixelEvent[] | null;
  /** The audiences the owner made in their Meta ad account; null while Meta is asked or when it cannot be. */
  custom: CustomAudience[] | null;
  /** Which of the two Meta did not answer. */
  unread: ("events" | "custom")[];
  editable: boolean;
  save: (next: Audience, summary: string) => void;
  onAdd: () => void;
}) {
  if (!view) return <p className="hint">Loading the client lists…</p>;
  const ticked = aud.lists.map((t) => ({ t, l: listFor(view.lists, t.id) })).filter((x) => x.l);
  const rest = view.lists.filter((l) => !aud.lists.some((t) => listFor(view.lists, t.id)?.id === l.id) && l.state !== "failed" && l.state !== "draft");
  const setTick = (id: string, patch: Partial<Audience["lists"][number]>, summary: string) =>
    save({ ...aud, lists: aud.lists.map((t) => (t.id === id ? { ...t, ...patch } : t)) }, summary);

  return (
    <div className="lists">
      {!view.lists.length && <p className="hint">No client lists yet.</p>}
      {ticked.map(({ t, l }) => (
        <div key={t.id}>
          <div className="listcard">
            <span className="av">
              <Icon name="users" />
            </span>
            <div>
              <b>
                {l!.name}: {n(l!.rowsSent)}
              </b>
              <p>
                {l!.state === "ready"
                  ? [found(l!), likeLine(l!)].filter(Boolean).join(" · ")
                  : "Meta is matching them. This can take a few hours."}
              </p>
              {l!.state === "ready" && l!.rowsSent > 0 && !l!.belowFloor && (
                <div className="match">
                  <i style={{ width: `${Math.min(100, Math.round(((l!.size ?? 0) / l!.rowsSent) * 100))}%` }} />
                </div>
              )}
            </div>
            {editable && (
              <button className="btn ghost sm" type="button" onClick={() => save({ ...aud, lists: aud.lists.filter((x) => x.id !== t.id) }, `Client lists: ${l!.name} taken off`)}>
                Remove
              </button>
            )}
            <div className="gl-opts">
              <button
                className={`tick${t.start ? " on" : ""}`}
                type="button"
                aria-pressed={t.start}
                disabled={!editable}
                onClick={() => setTick(t.id, { start: !t.start, ...(t.start ? {} : { out: false }) }, `Client lists: ${t.start ? "not " : ""}starting with ${l!.name}`)}
              >
                <span className="bx">
                  <Icon name="check" />
                </span>
                Start with them
              </button>
              <button
                className={`tick${t.like ? " on" : ""}`}
                type="button"
                aria-pressed={t.like}
                disabled={!editable || (!t.like && !l!.likeReady)}
                onClick={() => setTick(t.id, { like: !t.like }, `Client lists: ${t.like ? "not " : ""}people like ${l!.name}`)}
              >
                <span className="bx">
                  <Icon name="check" />
                </span>
                Find people like them
              </button>
              <button
                className={`tick out${t.out ? " on" : ""}`}
                type="button"
                aria-pressed={t.out}
                disabled={!editable}
                onClick={() => setTick(t.id, { out: !t.out, ...(t.out ? {} : { start: false }) }, `Client lists: ${t.out ? "keep" : "leave out"} ${l!.name}`)}
              >
                <span className="bx">
                  <Icon name="check" />
                </span>
                Leave them out
              </button>
            </div>
          </div>
        </div>
      ))}
      {editable && (
        <div className="chips">
          {rest.map((l) => (
            <button
              className="add"
              type="button"
              key={l.id}
              onClick={() => save({ ...aud, lists: [...aud.lists, { id: l.id, start: false, like: l.likeReady && !l.belowFloor, likeRatio: 0.01, out: true }] }, `Client lists: ${l.name} added`)}
            >
              <Icon name="plus" />
              {l.name}
            </button>
          ))}
          <button className="add" type="button" onClick={onAdd}>
            <Icon name="upload" />
            Add a new list
          </button>
        </div>
      )}
      {view.pixel && <PixelEvents aud={aud} view={view} events={events} unread={unread.includes("events")} editable={editable} save={save} />}
      <MetaAudiences aud={aud} audiences={custom} unread={unread.includes("custom")} editable={editable} save={save} />
      {aud.why.lists && (
        <p className="why">
          <b>Why these:</b> {aud.why.lists}
        </p>
      )}
      <p className="privacy">
        <Icon name="shield" />
        Sent to Meta only to find matches: scrambled first, never kept by Flashadz. Meta deletes what it cannot match.
      </p>
    </div>
  );
}
